import { from, map, type Unsubscribable } from 'rxjs';

import { type AlertStateInfo, DataTopic, LoadingState, toDataFrame } from '@grafana/data';
import { config } from '@grafana/runtime';
import {
  SceneDataLayerBase,
  type SceneDataLayerProvider,
  type SceneDataLayerProviderState,
  sceneGraph,
  type SceneTimeRangeLike,
} from '@grafana/scenes';
import { createErrorNotification } from 'app/core/copy/appNotification';
import { notifyApp } from 'app/core/reducers/appNotification';
import { contextSrv } from 'app/core/services/context_srv';
import { getMessageFromError } from 'app/core/utils/errors';
import { dispatch } from 'app/store/store';
import { AccessControlAction } from 'app/types/accessControl';

import { getDashboardSceneFor } from '../utils/utils';

import {
  loadPanelAlertStateCandidates,
  promAlertStateToAlertState,
  selectMostSevereAlertCandidatePerPanel,
} from './loadPanelAlertStateCandidates';

interface AlertStatesDataLayerState extends SceneDataLayerProviderState {}

export class AlertStatesDataLayer
  extends SceneDataLayerBase<AlertStatesDataLayerState>
  implements SceneDataLayerProvider
{
  private hasAlertRules = true;
  private _timeRangeSub: Unsubscribable | undefined;
  public topic = DataTopic.AlertStates;

  public constructor(initialState: AlertStatesDataLayerState) {
    super({
      isEnabled: true,
      ...initialState,
      isHidden: true,
    });
  }

  public onEnable(): void {
    const timeRange = sceneGraph.getTimeRange(this);

    this._timeRangeSub = timeRange.subscribeToState(() => {
      this.runWithTimeRange(timeRange);
    });
  }

  public onDisable(): void {
    this._timeRangeSub?.unsubscribe();
  }

  public runLayer() {
    const timeRange = sceneGraph.getTimeRange(this);
    this.runWithTimeRange(timeRange);
  }

  private async runWithTimeRange(timeRange: SceneTimeRangeLike) {
    const dashboard = getDashboardSceneFor(this);
    const { uid } = dashboard.state;

    if (this.querySub) {
      this.querySub.unsubscribe();
    }

    if (!uid || !this.canWork(timeRange)) {
      return;
    }
    const alerStatesExecution = from(loadPanelAlertStateCandidates(uid)).pipe(
      map((candidates) => {
        this.hasAlertRules = candidates.length > 0;
        return selectMostSevereAlertCandidatePerPanel(candidates).map(
          ({ panelId, state, ruleUID }, id): AlertStateInfo => ({
            state: promAlertStateToAlertState(state),
            id,
            panelId,
            dashboardUID: uid,
            ruleUID,
          })
        );
      })
    );

    this.querySub = alerStatesExecution.subscribe({
      next: (stateUpdate) => {
        const frame = toDataFrame(stateUpdate);
        this.publishResults({
          state: LoadingState.Done,
          series: [
            {
              ...frame,
              meta: {
                ...frame.meta,
                dataTopic: DataTopic.AlertStates,
              },
            },
          ],
          timeRange: timeRange.state.value,
        });
      },
      error: (err) => {
        this.handleError(err);
        this.publishResults({
          state: LoadingState.Error,
          series: [],
          errors: [
            {
              message: getMessageFromError(err),
            },
          ],
          timeRange: timeRange.state.value,
        });
      },
    });
  }

  private canWork(timeRange: SceneTimeRangeLike): boolean {
    const dashboard = getDashboardSceneFor(this);
    const { uid } = dashboard.state;

    if (!uid) {
      return false;
    }

    // Cannot fetch rules while on a public dashboard since it's unauthenticated
    if (config.publicDashboardAccessToken) {
      return false;
    }

    if (timeRange.state.value.raw.to !== 'now') {
      return false;
    }

    if (this.hasAlertRules === false) {
      return false;
    }

    const hasRuleReadPermission =
      contextSrv.hasPermission(AccessControlAction.AlertingRuleRead) &&
      contextSrv.hasPermission(AccessControlAction.AlertingRuleExternalRead);

    if (!hasRuleReadPermission) {
      return false;
    }

    return true;
  }

  private handleError = (err: unknown) => {
    const notification = createErrorNotification('AlertStatesDataLayer', getMessageFromError(err));
    dispatch(notifyApp(notification));
  };
}
